import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import FormGroup from 'ui-library/bs/FormGroup';
import {ButtonStyle} from 'ui-library/buttons/Button';
import useFormInput from 'ui-library/hooks/useFormInput';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import TextField from 'ui-library/inputs/TextField';
import Modal from 'ui-library/modals/Modal';
import {JSONValue} from 'util/Types';

import {AnalyticsParameterConfigGraphQL} from '../../../graphql-fragments/insights/__generated/AnalyticsParameterConfigGraphQL';
import AnalyticsParameterTypeDropdown from './AnalyticsParameterTypeDropdown';
import getParameterTypeAndConfigSetting from './data/getParameterTypeAndConfigSetting';
import {
  CONFIG_TYPENAME_TO_PARAMETER_TYPE,
  PARAMETER_TYPE_ALLOWS_MULTIPLE_VALUES,
  PARAMETER_TYPE_TO_CONFIG_TYPENAME,
} from './ParameterConstants';
import ParameterInfoButton from './ParameterInfoButton';
import ParameterTypeHelpButton from './ParameterTypeHelpButton';
import {ParameterTypeAndConfigSetting} from './ParameterTypes';
import trackParameterTypes from './trackParameterTypes';

export interface AnalyticsParameterConfigModalProps {
  initialConfig: AnalyticsParameterConfigGraphQL;
  initialValueJson: JSONValue;
  disabled: boolean;
  onRequestClose: () => void;
  onRequestSubmit: (newConfig: AnalyticsParameterConfigGraphQL) => void;
}

export default function AnalyticsParameterConfigModal({
  initialConfig,
  initialValueJson,
  disabled,
  onRequestClose,
  onRequestSubmit,
}: AnalyticsParameterConfigModalProps): JSX.Element {
  const systemNameFormInput = useFormInput(initialConfig.systemName);
  const [typeAndConfigSetting, setTypeAndConfigSetting] = useState<ParameterTypeAndConfigSetting>(
    getParameterTypeAndConfigSetting(initialConfig)
  );

  function handleSubmit(): void {
    onRequestSubmit({
      ...initialConfig,
      __typename: PARAMETER_TYPE_TO_CONFIG_TYPENAME[typeAndConfigSetting.type],
      systemName: systemNameFormInput.value,
      ...typeAndConfigSetting.configSetting,
    } as AnalyticsParameterConfigGraphQL);
    const previousType = CONFIG_TYPENAME_TO_PARAMETER_TYPE[initialConfig.__typename];
    if (previousType !== typeAndConfigSetting.type) {
      trackParameterTypes(previousType, typeAndConfigSetting.type);
    }
  }

  function currentStateWillDropValues(): boolean {
    return (
      !typeAndConfigSetting.configSetting.isMulti &&
      Array.isArray(initialValueJson) &&
      initialValueJson.length > 1
    );
  }

  return (
    <Modal
      buttons={[
        {
          disabled: disabled || systemNameFormInput.value === '',
          style: ButtonStyle.SECONDARY,
          submit: true,
          text: 'Update',
        },
      ]}
      onRequestClose={onRequestClose}
      onRequestSubmit={handleSubmit}
      title="Parameter configuration"
    >
      <FormGroup>
        <TextField
          inputClassName={css(styles.inputHeightFix)}
          label="Name"
          {...systemNameFormInput}
          disabled={disabled}
        />
      </FormGroup>
      <FormGroup>
        <FieldLabel label="Type" />
        <ParameterTypeHelpButton className="u-mini-spacing-left" />
        <AnalyticsParameterTypeDropdown
          parameterConfig={typeAndConfigSetting}
          onChange={setTypeAndConfigSetting}
          disabled={disabled}
        />
      </FormGroup>
      {PARAMETER_TYPE_ALLOWS_MULTIPLE_VALUES[typeAndConfigSetting.type] && (
        <FormGroup className={css(styles.checkboxFormGroup)}>
          <CheckboxInput
            id="multiple-values-checkbox"
            label="Allow multiple values"
            disabled={disabled}
            onChange={(e) => {
              const newTypeAndConfigSetting = {
                ...typeAndConfigSetting,
                configSetting: {
                  ...typeAndConfigSetting.configSetting,
                  isMulti: e.target.checked,
                },
              } as ParameterTypeAndConfigSetting;
              setTypeAndConfigSetting(newTypeAndConfigSetting);
            }}
            checked={typeAndConfigSetting.configSetting.isMulti || false}
          />
          <ParameterInfoButton />
        </FormGroup>
      )}
      {currentStateWillDropValues() && (
        <InlineBanner
          bannerType={InlineBannerType.WARNING}
          text="This parameter has multiple values selected. If you remove the multi-value setting, all values except the first one will be removed."
        />
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  inputHeightFix: {
    height: 36,
  },
  checkboxFormGroup: {
    display: 'flex',
  },
});
